/**
 * Service Worker Integration - Phase 3: Service Worker Resurrection
 * Connects service worker caching with PerformanceContext
 */

import { cacheManager } from './cacheManager';
import { productionLogger } from './production-logger';

interface ServiceWorkerIntegrationOptions {
  enablePerformanceTracking?: boolean;
  enableCacheWarmup?: boolean;
  enableAnalytics?: boolean;
}

export class ServiceWorkerIntegration {
  private options: ServiceWorkerIntegrationOptions;
  private performanceUpdateCallback?: (metrics: any) => void;

  constructor(options: ServiceWorkerIntegrationOptions = {}) {
    this.options = {
      enablePerformanceTracking: true,
      enableCacheWarmup: true,
      enableAnalytics: true,
      ...options
    };
  }

  /**
   * Initialize service worker integration with performance context
   */
  async initialize(performanceUpdateCallback?: (metrics: any) => void) {
    this.performanceUpdateCallback = performanceUpdateCallback;

    try {
      // Wait for service worker to be ready
      if ('serviceWorker' in navigator) {
        await navigator.serviceWorker.ready;
        productionLogger.info('🔗 Service Worker integration initialized');

        // Setup performance monitoring
        if (this.options.enablePerformanceTracking) {
          this.startPerformanceMonitoring();
        }

        // Warm up critical caches
        if (this.options.enableCacheWarmup) {
          await this.warmupCriticalCaches();
        }

        return true;
      }
    } catch (error) {
      productionLogger.error('❌ Service Worker integration failed:', error);
      return false;
    }

    return false;
  }

  /**
   * Start monitoring cache performance and reporting to PerformanceContext
   */
  private startPerformanceMonitoring() {
    const monitorInterval = setInterval(async () => {
      try {
        const cacheMetrics = await cacheManager.getCachePerformanceSummary();
        
        if (cacheMetrics && this.performanceUpdateCallback) {
          this.performanceUpdateCallback({
            cacheHitRate: cacheMetrics.hitRate,
            totalCacheRequests: cacheMetrics.totalRequests,
            cacheErrors: cacheMetrics.errors
          });
        }
      } catch (error) {
        productionLogger.warn('⚠️ Cache performance monitoring error:', error);
      }
    }, 30000); // Every 30 seconds

    // Clean up on page unload
    window.addEventListener('beforeunload', () => {
      clearInterval(monitorInterval);
    });
  }

  /**
   * Warm up critical caches for better initial performance
   */
  private async warmupCriticalCaches() {
    try {
      await cacheManager.warmupCriticalRoutes();
      productionLogger.info('🔥 Critical caches warmed up');
    } catch (error) {
      productionLogger.warn('⚠️ Cache warmup failed:', error);
    }
  }

  /**
   * Get real-time cache analytics
   */
  async getCacheAnalytics() {
    try {
      return await cacheManager.getCachePerformanceSummary();
    } catch (error) {
      productionLogger.error('❌ Failed to get cache analytics:', error);
      return null;
    }
  }

  /**
   * Preload specific routes (for navigation optimization)
   */
  async preloadRoutes(routes: string[]) {
    for (const route of routes) {
      try {
        await cacheManager.preloadRoute(route);
      } catch (error) {
        productionLogger.warn(`⚠️ Failed to preload route ${route}:`, error);
      }
    }
  }

  /**
   * Clear specific cache type
   */
  async clearCache(cacheType: 'admin' | 'products' | 'static' | 'api') {
    try {
      await cacheManager.clearCache(cacheType);
      productionLogger.info(`🗑️ Cache cleared: ${cacheType}`);
    } catch (error) {
      productionLogger.error(`❌ Failed to clear cache ${cacheType}:`, error);
    }
  }

  /**
   * Update service worker (force refresh)
   */
  async updateServiceWorker() {
    try {
      await cacheManager.updateServiceWorker();
      productionLogger.info('🔄 Service Worker update requested');
    } catch (error) {
      productionLogger.error('❌ Service Worker update failed:', error);
    }
  }
}

// Export singleton instance
export const serviceWorkerIntegration = new ServiceWorkerIntegration();